/**
 * @file utils.ts
 *
 * @fileoverview Helps methods regarding getting values from configuration objects (inside /config folder).
 */

import { EntityConfigField, EntityGroupConfig } from '~/jobiqo/models/config';
import { FieldGroupInfo } from '~/jobiqo/models/forms';
import {
  mapTermFieldMultiple,
  mapTermFieldSingle,
} from '../services/taxonomyHelpers';

/**
 * Returns the processed field group information.
 *
 * A groupInfo is a collection of fields that make up what
 * could be considered a "Field collection" for the frontend.
 * This function will read the configuration in the "config" folder for the entity
 * and map the fields to what the "Entity form" understands.
 *
 * In the end what it will do is clean up the "fields" property in the config object so that
 * it only provides the "Drupal" version of the fields (the keys).
 *
 * @param {EntityConfig} groupInfo
 *   The name of the group to load.
 *
 * @return {FieldGroupInfo}
 *   The FieldGroupInfo object.
 */
export const getGroup = (groupInfo: EntityGroupConfig): FieldGroupInfo => {
  const groupWithFields = {
    ...groupInfo,
    fields: Object.keys(groupInfo.fields).filter(
      (field) => groupInfo.fields[field],
    ),
  };
  return groupWithFields;
};

/**
 * Map the drupal entity to the API version (field mapping - reverse of getDrupalEntity) for a single group.
 *
 * This method will take a an entity and return the API version of the fields for those same values.
 * The fields in drupal might differ in names and formats to the ones
 * in the API. This method can be used when we have a response from the entity form and we
 * want to run a mutation to the API where the API field names should be used.
 *
 * @param {EntityConfig} groupInfo
 *  The group of fields being used.
 *
 * @param {any} entity
 *  The entity we get from the entity form with the responses (the formData response).
 */
export const getDrupalEntity = (
  groupInfoFields: FieldGroupInfo[],
  entity: any,
) => {
  let values = {};
  groupInfoFields.forEach((groupInfoField) => {
    values = {
      ...values,
      ...getDrupalGroupEntity(groupInfoField as any, entity),
    };
  });
  return values;
};

/**
 * Map an entity to the drupal version (field mapping) for a single field group.
 *
 * This method will take a set of fields and return the drupal version
 * of that same set of fields. The fields in drupal might differ in names and formats to the ones
 * in the API. This method will convert the API version to a drupal version so that we can feed
 * it to the entity form.
 *
 * Our entity form solution takes a drupal entity (as is) in order to make an "Edit form". Since from the
 * API we get a "schema entity" this will do the mapping to transform it to the drupal version.
 * The "config" object will be used to understand which field maps to what (API -> Drupal).
 *
 * @parma {EntityConfig} groupInfo
 *  The name of the group we are mapping.
 *
 * @param {any} apiEntity
 *  The raw object (set of entity fields).
 */
export const getDrupalGroupEntity = (
  groupInfo: EntityGroupConfig,
  apiEntity: any,
) => {
  const fieldKeys: string[] = Object.keys(groupInfo.fields);
  let returnValue = {};
  for (const fieldKey of fieldKeys) {
    // Check if its an objet go through the object and assign values.
    // (e.g. Address fields)
    if (typeof groupInfo.fields[fieldKey] === 'object') {
      returnValue = {
        ...returnValue,
        [fieldKey]: getSubFields(fieldKey, groupInfo, apiEntity),
      };
      continue;
    }

    // Since its not an object (nested fields) we can now know its a string and cast appropriately.
    const rawFieldName = groupInfo.fields[fieldKey] as string;

    // Check for single term references.
    if (apiEntity[rawFieldName] && apiEntity[rawFieldName].tid) {
      // Instead of calling with 1 we need a format like {targetId: 1}
      returnValue = {
        ...returnValue,
        [fieldKey]: mapTermFieldSingle(apiEntity[rawFieldName]),
      };
      continue;
    }

    // Check for mutiple term references.
    if (
      apiEntity[rawFieldName] &&
      apiEntity[rawFieldName].length > 0 &&
      apiEntity[rawFieldName][0].tid
    ) {
      // Instead of calling with [1] we need a format like [{targetId: 1}]
      returnValue = {
        ...returnValue,
        [fieldKey]: mapTermFieldMultiple(apiEntity[rawFieldName]),
      };
      continue;
    }

    // Check for logo fields.
    if (
      apiEntity[rawFieldName] &&
      apiEntity[rawFieldName].__typename === 'Image'
    ) {
      if (apiEntity[rawFieldName].url) {
        returnValue = {
          ...returnValue,
          [fieldKey]: apiEntity[rawFieldName].url,
        };
      } else {
        // Set the image as null as it has a different format.
        // The user can upload one from the computer or the current one
        // will be kept.
        returnValue = {
          ...returnValue,
          [fieldKey]: null,
        };
      }
    }

    // At this point we have a plain field like a string or an Int we can just map it
    // directly.
    returnValue = {
      ...returnValue,
      [fieldKey]: apiEntity[rawFieldName],
    };
  }
  return returnValue;
};

/**
 * Gets the values for a complex field that includes subfields.
 *
 * Recursively go throug the fields until there are no
 * more or is just a plain string or int. Used when we call a complex field from
 * getDrupalEntity like an Address field that contains multiple subfields.
 *
 * @see getDrupalEntity().
 */
const getSubFields = (
  fieldKey: string,
  groupInfo: EntityGroupConfig,
  entity,
) => {
  const topLevelField = groupInfo.fields[fieldKey] as EntityConfigField;
  let returnValue = {};

  if (topLevelField) {
    const SubFieldKeys: string[] = Object.keys(topLevelField.fields);
    SubFieldKeys.forEach((subFieldKey) => {
      // Assign with the value from the entity passed.
      // Check if its an objet go through the object again and assign values.
      if (typeof topLevelField.fields[fieldKey] === 'object') {
        returnValue = {
          ...returnValue,
          [subFieldKey]: getSubFields(subFieldKey, groupInfo, entity),
        };
      } else {
        // If is not an object (another nested field) then its a string.
        const subFieldValue = topLevelField.fields[subFieldKey] as string;
        returnValue = {
          ...returnValue,
          [subFieldKey]: entity?.[topLevelField.name]?.[subFieldValue],
        };
      }
    });
  }

  return returnValue;
};

/**
 * Map the drupal entity to the API version (field mapping - reverse of getDrupalEntity) for a single group.
 *
 * This method will take a an entity and return the API version of the fields for those same values.
 * The fields in drupal might differ in names and formats to the ones
 * in the API. This method can be used when we have a response from the entity form and we
 * want to run a mutation to the API where the API field names should be used.
 *
 * @param {EntityConfig} groupInfo
 *  The group of fields being used.
 * @param {any} entity
 *  The entity we get from the entity form with the responses (the formData response).
 * @param {boolean} includeTaxonomyLabels
 *  Whether to include the taxonomy labels in the API response.
 *
 * @returns {any}
 *  The API entity values.
 */
export const getAPIEntityValues = (
  groupInfoFields: FieldGroupInfo[],
  entity: any,
  includeTaxonomyLabels = false,
) => {
  let values = {};
  groupInfoFields.forEach((groupInfoField) => {
    values = {
      ...values,
      ...getAPIEntityGroupValues(
        groupInfoField as any,
        entity,
        includeTaxonomyLabels,
      ),
    };
  });
  return values;
};

/**
 * Map the drupal entity to the API version (field mapping - reverse of getDrupalEntity) for a single group.
 *
 * This method will take a an entity and return the API version of the fields for those same values.
 * The fields in drupal might differ in names and formats to the ones
 * in the API. This method can be used when we have a response from the entity form and we
 * want to run a mutation to the API where the API field names should be used.
 *
 * @param {EntityConfig} groupInfo
 *  The group of fields being used.
 * @param {any} entity
 *  The entity we get from the entity form with the responses (the formData response).
 * @param {boolean} includeTaxonomyLabels
 *  Whether to include the taxonomy labels in the API response.
 *
 * @returns {any}
 *  The API entity values.
 */
export const getAPIEntityGroupValues = (
  groupInfo: EntityGroupConfig,
  entity: any,
  includeTaxonomyLabels = false,
) => {
  const fieldKeys: string[] = Object.keys(groupInfo.fields);
  let returnValue = {};
  for (const fieldKey of fieldKeys) {
    //  Get the right field key.
    const entityFieldAPIKey: string = groupInfo.fields[fieldKey] as string;

    if (!entityFieldAPIKey) {
      // IF the field is set to null to hide it wont have any API Key and
      // we just bail out.
      // e.g. {fieldKey: null}
      continue;
    }

    if (entity[fieldKey] && entity[fieldKey].tid && !entity[fieldKey].level) {
      // Single term references.
      // Check that is not a term level field because tid also exists there.
      // instead of passing something like {tid: 1, label:'whatever'}
      // simply pass 1 (the tid).
      returnValue = {
        ...returnValue,
        [entityFieldAPIKey]: includeTaxonomyLabels
          ? {
              tid: entity[fieldKey].tid,
              label: entity[fieldKey].name,
            }
          : entity[fieldKey].tid,
      };
      continue;
    }

    // Mutiple term references.
    // Check that is not a term level field because tid also exists there.
    if (
      entity[fieldKey] &&
      entity[fieldKey].length > 0 &&
      entity[fieldKey][0]?.tid &&
      !entity[fieldKey][0]?.level
    ) {
      // instead of passing something like [{tid: 1, label:'whatever'}]
      // simply pass [1] (the tid).
      returnValue = {
        ...returnValue,
        [entityFieldAPIKey]: entity[fieldKey].map((e) => {
          return includeTaxonomyLabels
            ? {
                tid: e.tid,
                label: e.label,
              }
            : e.tid;
        }),
      };
      continue;
    }

    // free terms
    if (
      entity[fieldKey] &&
      entity[fieldKey].length > 0 &&
      entity[fieldKey][0]?.term &&
      entity[fieldKey][0]?.level >= 0 &&
      entity[fieldKey][0]?.term?.tid === 0 // Free terms have a 0 as id.
    ) {
      // Send in the expected format.
      returnValue = {
        ...returnValue,
        [entityFieldAPIKey]: entity[fieldKey].map((item) => {
          return {
            level: item.level,
            label: item.term.label,
          };
        }),
      };
      continue;
    }

    // Multiple term level fields.
    if (
      entity[fieldKey] &&
      entity[fieldKey].length > 0 &&
      entity[fieldKey][0]?.term &&
      entity[fieldKey][0]?.level >= 0
    ) {
      // Send in the expected format.
      returnValue = {
        ...returnValue,
        [entityFieldAPIKey]: entity[fieldKey].map(
          ({ term, __typename, label, ...extraFields }) => {
            return {
              tid: term.tid,
              ...extraFields,
            };
          },
        ),
      };
      continue;
    }

    // Check if its an objet go through the object and assign values.
    // (e.g. Address fields)

    if (
      groupInfo.fields[fieldKey] &&
      typeof groupInfo.fields[fieldKey] === 'object'
    ) {
      // If its an object it is of type `EntityConfigField`.
      const topLevelField: EntityConfigField = groupInfo.fields[
        fieldKey
      ] as EntityConfigField;

      if (!entity?.[fieldKey] || Object.keys(entity?.[fieldKey]).length === 0) {
        // If its just an empty object, we need to set the value to undefined.
        // It needs to have values inside.
        returnValue = {
          ...returnValue,
          [topLevelField.name]: undefined,
        };
        continue;
      }

      returnValue = {
        ...returnValue,
        [topLevelField.name]: getSubValues(fieldKey, groupInfo, entity),
      };
      continue;
    }

    // At this point we have a plain field  which is represented as a string, we can cast.
    returnValue = {
      ...returnValue,
      [entityFieldAPIKey]: entity[fieldKey],
    };
  }
  return returnValue;
};

/**
 * Gets the values for a complex field that includes subfields.
 *
 * Recursively go throug the fields until there are no
 * more or is just a plain string or int. Used when we call a complex field from
 * getEntityValues like an Address field that contains multiple subfields.
 *
 * @see getEntityValues().
 */
const getSubValues = (
  fieldKey: string,
  groupInfo: EntityGroupConfig,
  entity,
) => {
  const topLevelField = groupInfo.fields[fieldKey] as EntityConfigField;

  let returnValue = {};
  if (topLevelField) {
    const SubFieldKeys: string[] = Object.keys(topLevelField.fields);
    SubFieldKeys.forEach((subFieldKey) => {
      // Check if its an objet go through the object and assign values.
      if (typeof topLevelField.fields[fieldKey] === 'object') {
        returnValue = {
          ...returnValue,
          [topLevelField.fields[subFieldKey] as string]: getSubValues(
            subFieldKey,
            groupInfo,
            entity,
          ),
        };
      } else {
        if (entity?.[fieldKey]) {
          returnValue = {
            ...returnValue,
            [topLevelField.fields[subFieldKey] as string]: entity[fieldKey][
              subFieldKey
            ],
          };
        }
      }
    });
  }

  return returnValue;
};

/**
 * Checks if a field is a private field based on the config value.
 */
export const isFieldPrivate = (
  fieldKey: string,
  groupInfo: EntityGroupConfig,
) => {
  return groupInfo.privateFields?.[fieldKey];
};

/**
 * Calculates if section should be hidden.
 * Based on API hiddenFields values.
 *
 * @param {EntityConfig} groupInfo
 *  The group of fields being used.
 *
 * @param {string[]} hiddenFieldsKey
 *  List of hidden fields keys.
 *
 * @returns { boolean }
 *  True if all fields are hidden, false otherwise.
 */
export const isSectionHidden = (
  groupInfo: EntityGroupConfig,
  hiddenFieldsKeys?: string[] | null,
): boolean => {
  if (!hiddenFieldsKeys?.includes) {
    return false;
  }
  let counter = 0;
  const keys = Object.keys(groupInfo.fields);

  for (let key of keys) {
    if (hiddenFieldsKeys.includes(key)) {
      counter++;
    }
  }

  return counter === keys.length;
};

/**
 * Sort the resume config taking into consideration the "order" property in config.
 *
 * Returns the exact same FieldGroupInfo
 * but sorted with the "order" property wherever used.
 *
 * @param {FieldGroupInfo} unorderedGroupInfo
 *  The unordered group info.
 *
 * @returns
 *  The ordered group info.
 */
export const sortConfig = (
  unorderedGroupInfo: FieldGroupInfo,
): FieldGroupInfo => {
  const groupInfo: any = Object.values(unorderedGroupInfo)
    .sort((a: any, b: any) => {
      const aOrder = a?.order ?? Object.keys(unorderedGroupInfo).indexOf(a.key);
      const bOrder = b?.order ?? Object.keys(unorderedGroupInfo).indexOf(b.key);

      if (aOrder < bOrder) {
        return -1;
      }
      if (aOrder > bOrder) {
        return 1;
      }

      return 0;
    })
    .reduce(function (acc: any, cur: any) {
      acc[cur.key] = cur;
      return acc;
    }, {});

  return groupInfo;
};
